import type { ChangeableProperties } from '../utils/shapeConfigUtils'
import type {
  AnchorSide,
  AnchorSpec,
  BlueprintDimKey,
  BlueprintLayout,
} from './types'

/**
 * Families whose backrest is a band along the top edge (y 0..backrest) and
 * whose arms sit on the left/right edge — the default backrest/armrest
 * anchors are geometrically right for these. Other types need a FAMILY entry
 * below or fall back to the input row under the canvas.
 */
const STRAIGHT_TYPE_PATTERN =
  /^(A[123]|AUFULL|AUPFULL|SOFA|FOTEL|E|OA|LCH|SCHEZLONG|SLEEP)/

export const isStraightBlueprintType = (type: string) =>
  STRAIGHT_TYPE_PATTERN.test(type)

// Geometry-only fallbacks when the value is empty (most shapes destructure
// armrestWidth = 10 / backrestWidth = 20).
const FALLBACK_BACKREST_CM = 20
const FALLBACK_ARMREST_CM = 10

// Dimension-line distances from the shape edge (px). Each side has an inner
// and an outer tier so two measurements starting at the same corner (e.g.
// armrest + width on top, corner part + backrest on a side) don't overlap.
const INNER_PX: Record<AnchorSide, number> = {
  top: 16,
  bottom: 20,
  left: 34,
  right: 34,
}
const OUTER_PX: Record<AnchorSide, number> = {
  top: 42,
  bottom: 48,
  left: 70,
  right: 70,
}

type Segment = Pick<AnchorSpec, 'from' | 'to' | 'side'> & { outer?: boolean }

/** Shape-local cm geometry handed to the family formulas. */
interface Geometry {
  W: number
  L: number
  /** backrest depth (value or fallback) */
  b: number
  /** corner_part_length as stored (null = shape default) */
  cpl: number | null | undefined
  /** length as stored (null = shape default) */
  len: number | null | undefined
  /** corner_radius as stored (null = shape default) */
  radius: number | null | undefined
  /** angle as stored (null = shape default) */
  angle: number | null | undefined
}

/**
 * Per-family anchors derived from each shape's own drawing formulas
 * (SofaDrawingElements/SofaElements/<TYPE>.tsx — read only; portal-synced).
 */
interface FamilyGeometry {
  backrest?: (g: Geometry) => Segment
  cornerPart?: (g: Geometry) => Segment
  /** Side for the overall Length line (default left). */
  lengthSide?: 'left' | 'right'
  /** Replaces the bbox Length line (shapes whose bbox ≠ length). */
  length?: (g: Geometry) => Segment
  /** corner_radius on the drawing (chained after Length). */
  cornerRadius?: (g: Geometry) => Segment
  /** angle on the drawing (sweep around a centre). */
  angle?: (g: Geometry) => NonNullable<AnchorSpec['angle']>
}

// CORNERL/R, CORNERCUTL: L-shaped backrest (top band + one side leg running
// to the bottom edge); the leg's depth is measured where it meets the bottom.
const cornerLegBottom =
  (onRight: boolean) =>
  ({ W, L, b }: Geometry): Segment => ({
    from: { x: onRight ? W - b : 0, y: L },
    to: { x: onRight ? W : b, y: L },
    side: 'bottom',
  })

// OTTP1L/R, OTKCMANYPILLOWL/R: backrest leg on one side that stops short of
// the bottom edge (H / 1.6, resp. 0.6 H + b); measured on the top edge, over
// the leg. Takes the top inner tier, so Width moves to the outer one.
const legTop =
  (onRight: boolean) =>
  ({ W, b }: Geometry): Segment => ({
    from: { x: onRight ? W - b : 0, y: 0 },
    to: { x: onRight ? W : b, y: 0 },
    side: 'top',
  })

// OTKCMANYPILLOWL/R: corner_part_length draws a full-width separator line at
// y = corner_part_length (default: the shape width) — measured from the top
// on the right edge.
const separatorRight = ({ W, L, cpl }: Geometry): Segment => ({
  from: { x: W, y: 0 },
  to: { x: W, y: Math.min(cpl ?? W, L) },
  side: 'right',
})

// ANGLECORNERLCH*: the straight side edge runs from the top down to
// y = corner_part_length (component defaults: L 80, R 20), and the backrest
// band meets that same edge. ANGLECORNERLCHL draws mirrored (scaleX -1), so
// its edge is the right one.
const angleLchEdge = (
  onRight: boolean,
  defaultCpl: number
): FamilyGeometry => ({
  lengthSide: onRight ? 'left' : 'right',
  cornerPart: ({ W, L, cpl }) => ({
    from: { x: onRight ? W : 0, y: 0 },
    to: { x: onRight ? W : 0, y: Math.min(cpl ?? defaultCpl, L) },
    side: onRight ? 'right' : 'left',
  }),
  backrest: ({ W, b }) => ({
    from: { x: onRight ? W : 0, y: 0 },
    to: { x: onRight ? W : 0, y: b },
    side: onRight ? 'right' : 'left',
    outer: true,
  }),
})

// ARCHCORNERL: arc sector whose circle centre sits on the straight left edge
// at y = length + corner_radius (the group bbox is that outer radius). The
// left edge runs top → length (blue connector at the top), then the inner
// arc of radius corner_radius starts. Height falsy → 100; corner_radius
// undefined → 20, clamped to [0, length]. Backrest is the band along the
// outer arc, meeting the left edge at y 0..backrest.
const archLength = ({ len }: Geometry) => (len ? len : 100)
const ARCHCORNERL: FamilyGeometry = {
  length: g => ({
    from: { x: 0, y: 0 },
    to: { x: 0, y: archLength(g) },
    side: 'left',
  }),
  cornerRadius: g => {
    const len = archLength(g)
    const r = Math.min(Math.max(g.radius ?? 20, 0), len)
    return {
      from: { x: 0, y: len },
      to: { x: 0, y: len + r },
      side: 'left',
    }
  },
  backrest: ({ b }) => ({
    from: { x: 0, y: 0 },
    to: { x: 0, y: b },
    side: 'left',
    outer: true,
  }),
  // `angle` (default 90) is the sector's sweep, measured clockwise from the
  // straight left edge (pointing up) around the arc centre; the red
  // connector sits at the end of the sweep on the outer radius.
  angle: g => {
    const len = archLength(g)
    const r = Math.min(Math.max(g.radius ?? 20, 0), len)
    return {
      center: { x: 0, y: len + r },
      radius: len + r,
      sweepDeg: g.angle ?? 90,
    }
  },
}

const FAMILY: Record<string, FamilyGeometry> = {
  ARCHCORNERL,
  CORNERR: { backrest: cornerLegBottom(true) },
  CORNERL: { backrest: cornerLegBottom(false) },
  CORNERCUTL: {
    backrest: cornerLegBottom(false),
    // Cut runs (W, cpl) → (cpl, L); without a value the shape uses
    // L * (1 - ANGLE_RADIUS) with ANGLE_RADIUS = 0.25.
    cornerPart: ({ W, L, cpl }) => ({
      from: { x: W, y: 0 },
      to: { x: W, y: Math.min(cpl || L * 0.75, L) },
      side: 'right',
    }),
  },
  OTTP1L: { backrest: legTop(false) },
  OTTP1R: { backrest: legTop(true) },
  OTKCMANYPILLOWL: { backrest: legTop(false), cornerPart: separatorRight },
  OTKCMANYPILLOWR: { backrest: legTop(true), cornerPart: separatorRight },
  ANGLECORNERLCHL: angleLchEdge(true, 80),
  ANGLECORNERLCHR: angleLchEdge(false, 20),
  // Right leg reaches the bottom edge at x W-b..W; the bottom seat edge runs
  // from x = W - corner_part_length to W (cut-off = W - cpl).
  ANGLECORNERFLAT: {
    backrest: ({ W, L, b }) => ({
      from: { x: W - b, y: L },
      to: { x: W, y: L },
      side: 'bottom',
    }),
    cornerPart: ({ W, L, cpl }) => ({
      from: { x: Math.max(0, W - (cpl ?? W)), y: L },
      to: { x: W, y: L },
      side: 'bottom',
      outer: true,
    }),
  },
}

/** Types with on-drawing anchors beyond the straight defaults. */
export const FAMILY_ANCHOR_TYPES = Object.keys(FAMILY)

/**
 * corner_radius / angle are edited on the drawing only where a family places
 * them; elsewhere they stay in the field grid.
 */
export const familyDrawsKey = (type: string, key: string) =>
  (key === 'corner_radius' && !!FAMILY[type]?.cornerRadius) ||
  (key === 'angle' && !!FAMILY[type]?.angle)

export const DIM_LABELS: Record<BlueprintDimKey, string> = {
  width: 'Width',
  length: 'Length',
  backrest_width: 'Backrest width',
  armrest_width: 'Armrest width',
  extendable_part_length: 'Extension length',
  corner_part_length: 'Corner part length',
  corner_radius: 'Corner radius',
  angle: 'Angle',
}

export interface AnchorContext {
  layout: BlueprintLayout
  changeable: ChangeableProperties
  values: Partial<Record<BlueprintDimKey, number | null | undefined>>
}

export interface ResolvedAnchors {
  /** Drawn on the blueprint, in this (= tab) order. */
  anchors: AnchorSpec[]
  /** Editable but not (yet) placeable on this shape: shown as a small row. */
  fallbackKeys: BlueprintDimKey[]
}

export const showsArmrest = (ctx: AnchorContext) =>
  !!ctx.changeable.armrest_width && ctx.layout.armrestPosition.trim() !== ''

/** Shown above each input on hover/focus: what the dashed line measures. */
const SHORT_HINTS: Record<BlueprintDimKey, string> = {
  width: 'width',
  length: 'length',
  backrest_width: 'backrest',
  armrest_width: 'armrest',
  extendable_part_length: 'extension',
  corner_part_length: 'corner part',
  corner_radius: 'radius',
  angle: 'angle',
}

const toAnchor = (
  key: BlueprintDimKey,
  { outer, ...seg }: Segment
): AnchorSpec => ({
  key,
  label: DIM_LABELS[key],
  hint: SHORT_HINTS[key],
  ...seg,
  offsetPx: (outer ? OUTER_PX : INNER_PX)[seg.side],
})

export function resolveAnchors(ctx: AnchorContext): ResolvedAnchors {
  const { layout, changeable, values } = ctx
  if (!layout.ready || layout.scale <= 0)
    return { anchors: [], fallbackKeys: [] }

  const isComposite = layout.type === 'COMPOSITE'
  const { scale, shapeRect } = layout
  const W = shapeRect.width / scale
  const L = shapeRect.height / scale
  const geometry: Geometry = {
    W,
    L,
    b: Math.min(values.backrest_width ?? FALLBACK_BACKREST_CM, L, W),
    cpl: values.corner_part_length,
    len: values.length,
    radius: values.corner_radius,
    angle: values.angle,
  }

  const family = isComposite ? undefined : FAMILY[layout.type]
  const straight = isStraightBlueprintType(layout.type)
  const armrest = !isComposite && showsArmrest(ctx)
  const armOnTop = armrest && straight

  // Dimension-specific anchors first, so Width/Length can yield their inner
  // tier when a family/armrest line claims it.
  const extra: AnchorSpec[] = []
  const fallbackKeys: BlueprintDimKey[] = []

  if (!isComposite) {
    // Extendable part, chained under Length on the same left-hand line.
    const ext = layout.extension
    if (ext && ext.height > 0) {
      const top = (ext.y - shapeRect.y) / scale
      const left = (ext.x - shapeRect.x) / scale
      extra.push(
        toAnchor('extendable_part_length', {
          from: { x: left, y: top },
          to: { x: left, y: top + ext.height / scale },
          side: 'left',
        })
      )
    } else if (changeable.extendable_part_length) {
      fallbackKeys.push('extendable_part_length')
    }

    if (changeable.backrest_width) {
      if (family?.backrest) {
        extra.push(toAnchor('backrest_width', family.backrest(geometry)))
      } else if (straight) {
        extra.push(
          toAnchor('backrest_width', {
            from: { x: W, y: 0 },
            to: { x: W, y: geometry.b },
            side: 'right',
          })
        )
      } else {
        fallbackKeys.push('backrest_width')
      }
    }

    if (changeable.corner_part_length) {
      if (family?.cornerPart) {
        extra.push(toAnchor('corner_part_length', family.cornerPart(geometry)))
      } else {
        fallbackKeys.push('corner_part_length')
      }
    }

    // Only where the family places it; elsewhere corner_radius stays in the
    // field grid (it is a plain rounding radius on most shapes).
    if (changeable.corner_radius && family?.cornerRadius) {
      extra.push(toAnchor('corner_radius', family.cornerRadius(geometry)))
    }
    if (changeable.angle && family?.angle) {
      extra.push({
        key: 'angle',
        label: DIM_LABELS.angle,
        hint: SHORT_HINTS.angle,
        unit: '°',
        from: { x: 0, y: 0 },
        to: { x: 0, y: 0 },
        side: 'left',
        angle: family.angle(geometry),
      })
    }

    if (armrest) {
      if (armOnTop) {
        const arm = Math.min(values.armrest_width ?? FALLBACK_ARMREST_CM, W)
        const onRight = layout.armrestPosition.trim().toUpperCase() === 'R'
        extra.push(
          toAnchor('armrest_width', {
            from: { x: onRight ? W - arm : 0, y: 0 },
            to: { x: onRight ? W : arm, y: 0 },
            side: 'top',
          })
        )
      } else {
        fallbackKeys.push('armrest_width')
      }
    }
  }

  const innerTaken = (side: AnchorSide) =>
    extra.some(a => a.side === side && a.offsetPx === INNER_PX[side])

  const anchors: AnchorSpec[] = []
  if (changeable.width || isComposite) {
    anchors.push({
      ...toAnchor('width', {
        from: { x: 0, y: 0 },
        to: { x: W, y: 0 },
        side: 'top',
        outer: innerTaken('top'),
      }),
      readOnly: isComposite,
    })
  }
  if (changeable.length || isComposite) {
    const side = family?.lengthSide ?? 'left'
    const x = side === 'left' ? 0 : W
    const segment: Segment = family?.length?.(geometry) ?? {
      from: { x, y: 0 },
      to: { x, y: L },
      side,
    }
    anchors.push({
      ...toAnchor('length', {
        ...segment,
        // Extension / corner radius chain onto Length on the same (inner)
        // line; anything else on that inner line pushes Length outward.
        outer: extra.some(
          a =>
            a.side === segment.side &&
            a.key !== 'extendable_part_length' &&
            a.key !== 'corner_radius' &&
            a.offsetPx === INNER_PX[segment.side]
        ),
      }),
      readOnly: isComposite,
    })
  }

  return { anchors: [...anchors, ...extra], fallbackKeys }
}
